"use client"

import { useState } from "react"
import { format } from "date-fns"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Calendar } from "@/shared/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/shared/components/ui/popover"
import { cn } from "@/shared/lib/utils"
import { useIsIOS } from "@/shared/hooks/use-is-ios"

// Helper to parse YYYY-MM-DD string as a local date (avoids timezone shifting)
const parseLocalDate = (dateString: string): Date => {
    const [year, month, day] = dateString.split('-').map(Number)
    return new Date(year, month - 1, day)
}

// Helper to format date to YYYY-MM-DD using local timezone (avoids timezone shifting)
const formatDateForDB = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// Helper to convert date to Date object (handles both Date and string)
const toDate = (date: Date | string | null | undefined): Date | undefined => {
    if (!date) return undefined
    if (date instanceof Date) return date
    if (typeof date === 'string') return parseLocalDate(date)
    return undefined
}

// Helper to convert Date to storage format (Date or string)
const toStorageFormat = <T extends 'date' | 'string'>(
    date: Date | null,
    format: T
): T extends 'date' ? Date | null : string | null => {
    if (!date) return null as any
    if (format === 'date') return date as any
    return formatDateForDB(date) as any
}

interface WorkOrderHoursDatepickerProps {
    /** Date value - can be Date object or YYYY-MM-DD string */
    value: Date | string | null
    /** Callback when date is selected */
    onSelect: (date: Date | null) => void
    /** Whether the datepicker is disabled */
    disabled?: boolean
    /** Additional CSS classes for the button */
    className?: string
    /** Additional inline styles for the button */
    style?: React.CSSProperties
    /** Whether to show error state (red border) */
    isError?: boolean
    /** Whether to show modified state (purple border) */
    isModified?: boolean
    /** Placeholder text when no date is selected */
    placeholder?: string
    /** Storage format - 'date' for Date objects, 'string' for YYYY-MM-DD strings */
    storageFormat?: 'date' | 'string'
}

export function WorkOrderHoursDatepicker({
    value,
    onSelect,
    disabled = false,
    className,
    style,
    isError = false,
    isModified = false,
    placeholder = "Pick date",
    storageFormat = 'date',
}: WorkOrderHoursDatepickerProps) {
    const isIOS = useIsIOS()
    const dateValue = toDate(value)
    const [isOpen, setIsOpen] = useState(false)

    const handleDateSelect = (date: Date | undefined) => {
        if (storageFormat === 'string') {
            // If storing as string, convert to string format
            const stringDate = date ? formatDateForDB(date) : null
            // Call onSelect with Date object, parent will handle conversion
            onSelect(date || null)
        } else {
            // If storing as Date, pass Date directly
            onSelect(date || null)
        }
    }

    if (isIOS) {
        return (
            <input
                type="date"
                disabled={disabled}
                value={dateValue ? formatDateForDB(dateValue) : ""}
                onChange={(e) => {
                    const val = e.target.value
                    handleDateSelect(val ? parseLocalDate(val) : undefined)
                }}
                placeholder={placeholder}
                className={cn(
                    "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
                    !value && "text-muted-foreground",
                    isModified && !isError && "border-2",
                    isError && "border-2 border-red-500",
                    className
                )}
                style={isModified && !isError ? { borderColor: "#9c27b0", ...style } : style}
            />
        )
    }

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "w-full justify-start text-left font-normal",
                        !value && "text-muted-foreground",
                        isModified && !isError && "border-2",
                        isError && "border-2 border-red-500",
                        className
                    )}
                    style={isModified && !isError ? { borderColor: '#9c27b0', ...style } : style}
                    disabled={disabled}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                        {dateValue ? (
                            format(dateValue, "PPP")
                        ) : (
                            <span>{placeholder}</span>
                        )}
                    </span>
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={dateValue}
                    onSelect={handleDateSelect}
                    disabled={disabled}
                    initialFocus
                />
                <div className="p-2 border-t">
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="w-full h-7 text-xs"
                        onClick={() => setIsOpen(false)}
                    >
                        Done
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    )
}

// Export helper functions for use in components that need manual conversion
export { parseLocalDate, formatDateForDB }

